<template>
  <v-dialog
    :value="visiblePopup"
    :max-width="1500"
    @click:outside="onClose"
    @keydown.esc="onClose"
    content-class="geo-locations-dialog"
  >
    <div class="geo-locations-popup">
      <div class="geo-locations-popup__header">
        <div class="geo-locations-popup__header__title">{{$t(title)}}</div>
        <button
          class='geo-locations-popup__header__close'
          @click='onClose'
          aria-label="Close geographic distribution dialog"
        >
          <span class="material-icons-outlined">close</span>
        </button>
      </div>
      <div class="geo-locations-popup__content">
        <div class="geo-locations-popup__map">
          <bubblemap
            :data='coordinatesForIPs'
            :width='960'
            :height='540'
          />
        </div>
        <div class='geo-locations-popup__legend'>
          <div class='geo-locations-popup__legend__title'>{{$t(legendTitle)}}</div>
          <div class='geo-locations-popup__legend__count'>{{ Object.keys(locations).length }} {{ Object.keys(locations).length === 1 ? 'country' : 'countries' }}</div>
          <div class='geo-locations-popup__legend__list'>
            <div
              class='geo-locations-popup__legend__item'
              v-for='(country, i) in Object.keys(locations)'
              :key='`legend-item-${i}`'
            >
              <div class='geo-locations-popup__legend__item__flag'>
                <country-flag
                  rounded
                  :country='locations[country].countryCode'
                  size='normal' />
              </div>
              <div class='geo-locations-popup__legend__item__info'>
                <div class='geo-locations-popup__legend__item__name'>
                  {{ locations[country].countryName }}
                </div>
                <div class='geo-locations-popup__legend__item__data'>
                  <span class='geo-locations-popup__legend__item__tag'>IPv4: {{ locations[country].ipV4 }}</span>
                  <span class='geo-locations-popup__legend__item__tag'>IPv6: {{ locations[country].ipV6 }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </v-dialog>

</template>

<script>

import Bubblemap from '@/components/charts/Bubblemap.vue';
import CountryFlag from 'vue-country-flag';
import { mapGetters } from 'vuex';
import disableOuterScrollOnPopupVisible from '@/mixins/disableOuterScrollOnPopupVisible';

export default {
  name: 'GEOLocationsPopup',
  components: { CountryFlag, Bubblemap },
  mixins: [disableOuterScrollOnPopupVisible],
  props: {
    visiblePopup: {
      type: Boolean,
      required: true,
    },
    locations: {
      type: Object,
      required: true,
      default: () => {},
    },
  },
  computed: {
    ...mapGetters([
      'coordinatesOptions',
    ]),
    coordinatesForIPs() {
      return Object.values(this.coordinatesOptions);
    },
    title() {
      return 'geo.locations.popup.title';
    },
    legendTitle() {
      return 'geo.locations.popup.legend.title';
    },
  },
  methods: {
    onClose() {
      this.$emit('closeClicked', true);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.geo-locations-popup {
  padding: $space-6 $space-7;
  background-color: var(--bg-secondary, #fff);
  border-radius: $radius-lg;
  border: 1px solid var(--border-primary, transparent);

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: $space-4;

    &__title {
      color: var(--text-primary);
      font-family: $font-family-bold;
      font-size: $font-size-3xl;
      line-height: $line-height-tight;
      text-transform: capitalize;
    }

    &__close {
      @include flex-center;
      width: $space-8;
      height: $space-8;
      border: none;
      border-radius: $radius-full;
      background: transparent;
      color: var(--text-secondary);
      cursor: pointer;
      @include transition(background-color, $transition-duration-fast);

      &:hover {
        background-color: var(--bg-tertiary, rgba(0, 0, 0, 0.06));
        color: var(--text-primary);
      }

      .material-icons-outlined {
        font-size: $icon-lg;
      }
    }
  }

  &__content {
    display: flex;
    flex-direction: row;
    gap: $space-6;
    align-items: flex-start;
  }

  &__map {
    flex: 1;
    min-width: 0;
    border-radius: $radius-lg;
    overflow: hidden;
  }

  &__legend {
    flex: 0 0 rem(260);
    display: flex;
    flex-direction: column;

    &__title {
      color: var(--text-primary);
      font-family: $font-family-bold;
      font-size: $font-size-lg;
      line-height: $line-height-tight;
      text-transform: capitalize;
      margin-bottom: $space-1;
    }

    &__count {
      color: var(--text-secondary);
      font-family: $font-family-regular;
      font-size: $font-size-sm;
      margin-bottom: $space-4;
    }

    &__list {
      max-height: rem(480);
      overflow-y: auto;
      @include custom-scrollbar(4px, #888, transparent);
      padding-right: $space-2;
    }

    &__item {
      display: flex;
      align-items: center;
      padding: $space-2-5 $space-3;
      border-radius: $radius-6;
      @include transition(background-color, $transition-duration-fast);

      &:hover {
        background-color: var(--bg-tertiary, rgba(0, 0, 0, 0.04));
      }

      & + & {
        margin-top: $space-1;
      }

      &__flag {
        flex-shrink: 0;
        margin-right: $space-3;
      }

      &__info {
        display: flex;
        flex-direction: column;
        min-width: 0;
      }

      &__name {
        color: var(--text-primary);
        font-family: $font-family-semibold;
        font-size: $font-size-base;
        line-height: $line-height-snug;
        @include truncate;
      }

      &__data {
        display: flex;
        gap: $space-2;
        margin-top: $space-0-5;
      }

      &__tag {
        color: var(--text-secondary);
        font-family: $font-family-regular;
        font-size: $font-size-xs;
        line-height: $line-height-normal;
      }
    }
  }
}
</style>
